import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { useExpensesOwner } from "../../expenses/hooks/useExpensesOwner";
import PaidPeriodsRegistry from "./PaidPeriodsRegistry";
import PaidHistorySection from "./PaidHistorySection";
import PeriodsCatalogSection from "./PeriodsCatalogSection";

// ---------------------------------------------------------------------------
// Periodos pagados (#1141): una sola sección para lo que antes eran
// «Periodos de nómina» y «Historial pagado».
//
//   • Por periodo — el registro de pagos por quincena/mes, con colaboradores,
//     comprobantes y costo (todos; quien no es dueño ve solo lo suyo).
//   • Conciliación — la nómina contra los estados de cuenta (#1027), solo
//     dueños (rol owner + acceso a Finanzas); el servidor lo vuelve a exigir.
//   • Catálogo CFDI — los periodos que usan las liquidaciones timbradas.
// ---------------------------------------------------------------------------

type View = "registry" | "reconciliation" | "catalog";

export default function PaidPeriodsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const isOwner = useExpensesOwner() === true;
  const [view, setView] = useState<View>("registry");

  const views: { id: View; label: string; icon: string }[] = [
    { id: "registry", label: t("payroll.paidPeriods.viewRegistry"), icon: "ri-calendar-check-line" },
    ...(isOwner ? [{ id: "reconciliation" as View, label: t("payroll.paidPeriods.viewReconciliation"), icon: "ri-bank-line" }] : []),
    { id: "catalog", label: t("payroll.paidPeriods.viewCatalog"), icon: "ri-calendar-2-line" },
  ];
  const current = views.some((v) => v.id === view) ? view : "registry";

  return (
    <div className="w-full overflow-y-auto p-4 md:p-6" data-testid="payroll-paid-periods">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className={`text-xl font-semibold ${tc.text}`}>
            <i className="ri-calendar-check-line mr-2" />
            {t("payroll.paidPeriods.title")}
          </h2>
          <p className={`text-sm ${tc.textSecondary}`}>{t("payroll.paidPeriods.subtitle")}</p>
        </div>
        <div className={`inline-flex overflow-hidden rounded-lg border ${tc.border}`} role="tablist">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={current === v.id}
              data-testid={`view-${v.id}`}
              onClick={() => setView(v.id)}
              className={`flex items-center gap-1 px-3 py-2 text-sm ${current === v.id ? "bg-primary-500 text-white" : `${tc.bg} ${tc.text}`}`}
            >
              <i className={v.icon} />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {current === "registry" && <PaidPeriodsRegistry />}
      {current === "reconciliation" && <PaidHistorySection />}
      {current === "catalog" && <PeriodsCatalogSection />}
    </div>
  );
}
